import { useIsMobile } from "@/libs/hooks/useIsMobile";
import { useMainDict } from "@/libs/hooks/useTranslation";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTiktok,
  IconMapPin,
  IconNavigation,
  IconPhone,
  IconPhoneCall,
} from "@tabler/icons-react";
import { OpeningHoursModal } from "../OpeningHoursModal";
import { useSettings } from "@/libs/hooks/useSettings";

export type AddressAndPhoneProps = {
  phoneNumber: string | undefined;
  address: string | undefined;
  showOpeningHours: boolean | undefined;
};

export const AddressAndPhone: React.FC<AddressAndPhoneProps> = ({
  phoneNumber,
  address,
  showOpeningHours,
}) => {
  const { t } = useMainDict();
  const isMobile = useIsMobile();
  const { settings } = useSettings();

  return (
    <>
      {/* Action buttons */}
      <div className="flex flex-col md:flex-row md:flex-wrap items-center justify-center gap-3 mt-6 px-1 w-full">
        <a
          href="#menu-section"
          aria-label={t("showMenu")}
          className="vintage-btn vintage-btn-primary w-full md:w-auto"
        >
          {t("showMenu")}
        </a>

        <div className="flex w-full md:w-auto justify-center gap-3">
          {isMobile && (
            <a
              target="_blank"
              href={`tel:${phoneNumber?.replaceAll(" ", "")}`}
              aria-label={t("callUs")}
              className="vintage-btn w-full md:w-auto bg-vintage-olive text-vintage-cream shadow-[3px_3px_0px_rgba(92,107,77,0.5)] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[5px_5px_0px_rgba(92,107,77,0.5)] active:translate-x-[1px] active:translate-y-[1px]"
            >
              <IconPhoneCall className="mr-2" size={18} aria-hidden="true" />
              {t("callUs")}
            </a>
          )}
          <a
            target="_blank"
            href="https://www.google.com/maps/dir/?api=1&destination=Pizzicletta,Kalwaryjska 3,30-509 Kraków"
            aria-label={t("navigateToUs")}
            className="vintage-btn vintage-btn-secondary w-full md:w-auto"
          >
            <IconNavigation className="mr-2" size={18} aria-hidden="true" />
            {t("navigateToUs")}
          </a>
        </div>

        {showOpeningHours && <OpeningHoursModal />}
      </div>

      {/* Contact info with vintage styling */}
      <div className="mt-6 text-center">
        {/* Decorative divider */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-px bg-vintage-ochre/50" />
          <span className="text-vintage-ochre text-sm">✦</span>
          <div className="w-12 h-px bg-vintage-ochre/50" />
        </div>

        <div className="space-y-2 text-vintage-espresso">
          <div className="flex items-center justify-center gap-3 font-serif text-lg">
            <IconPhone size={20} className="text-vintage-terracotta" aria-hidden="true" />
            <span className="font-medium tracking-wide">{phoneNumber}</span>
          </div>
          <div className="flex items-center justify-center gap-3 font-serif text-lg">
            <IconMapPin size={20} className="text-vintage-terracotta" aria-hidden="true" />
            <span className="tracking-wide">{address}</span>
          </div>
        </div>
      </div>

      {/* Social links with vintage styling */}
      <div className="flex items-center justify-center gap-4 mt-6">
        {settings?.instagramLink && (
          <a
            target="_blank"
            href={settings.instagramLink}
            aria-label="Instagram"
            className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-vintage-burgundy text-vintage-burgundy hover:bg-vintage-burgundy hover:text-vintage-cream transition-all duration-300"
          >
            <IconBrandInstagram size={24} aria-hidden="true" />
          </a>
        )}
        {settings?.facebookLink && (
          <a
            target="_blank"
            href={settings.facebookLink}
            aria-label="Facebook"
            className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-vintage-burgundy text-vintage-burgundy hover:bg-vintage-burgundy hover:text-vintage-cream transition-all duration-300"
          >
            <IconBrandFacebook size={24} aria-hidden="true" />
          </a>
        )}
        {settings?.tiktokLink && (
          <a
            target="_blank"
            href={settings.tiktokLink}
            aria-label="TikTok"
            className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-vintage-burgundy text-vintage-burgundy hover:bg-vintage-burgundy hover:text-vintage-cream transition-all duration-300"
          >
            <IconBrandTiktok size={24} aria-hidden="true" />
          </a>
        )}
      </div>
    </>
  );
};
